/**
 * This file contains utility functions to work with Zod.
 */

import { mapValues as _mapValues } from 'lodash-es'
import { z } from 'zod'

/**
 * Converts an object's values into a typed tuple suitable for `z.enum()`.
 * Zod requires a `[T, ...T[]]` tuple, but `Object.values()` returns `T[]`.
 * This utility preserves the literal union type so form values stay narrow.
 *
 * @example
 * const Status = { Active: 'ACTIVE', Paused: 'PAUSED' } as const
 * z.enum(zodEnumValues(Status)) // z.ZodEnum<['ACTIVE', 'PAUSED']>
 */
export function zodEnumValues<T extends string>(
  object: Record<string, T>,
): [T, ...T[]] {
  const values = Object.values(object)
  if (values.length === 0) {
    throw new Error('zodEnumValues requires at least one value')
  }
  return values as [T, ...T[]]
}

/**
 * Extracts the default values from a given Zod schema.
 * In case values in schema don't have a `default` defined,
 * the default value will be `undefined`.
 *
 * @example
 * const schema = z.object({
 *   text: z.string().default(''),
 *   amount: z.number().default(1),
 *   list: z.array(z.string()).default(['1']),
 * })
 *
 * getSchemaDefaults(schema)
 * // Returns:
 * // {
 * //   text: '',
 * //   amount: 1,
 * //   list: ['1']
 * // }
 */
export const getSchemaDefaults = <T extends z.ZodRawShape>(
  schema: z.ZodObject<T>,
) => {
  return _mapValues(schema.shape, (shapeValue) =>
    shapeValue instanceof z.ZodDefault
      ? shapeValue._def.defaultValue()
      : undefined,
  ) as {
    [K in keyof T]: T[K] extends z.ZodDefault<infer D> ? D : undefined
  }
}

/**
 * Extracts the types from a given Zod schema.
 * In case values in schema don't have a `default` defined,
 * the default value will be `unknown`.
 *
 * @example
 * const schema = z.object({
 *   text: z.string().default(''),
 *   amount: z.number().default(1),
 *   list: z.array(z.string()).default(['1']),
 *   category: z.enum(['A', 'B', 'C']).default(['A']),
 * })
 *
 * getSchemaTypes(schema)
 * // Returns:
 * // {
 * //   text: 'string',
 * //   amount: 'number',
 * //   list: 'array of strings'
 * // }
 */
export const getSchemaTypes = <T extends z.ZodRawShape>(
  schema: z.ZodObject<T>,
) => {
  return Object.fromEntries(
    Object.entries(schema.shape).map(([key, shapeValue]) => {
      // Extract inner type if it's a default or nullable
      if (shapeValue instanceof z.ZodDefault) {
        shapeValue = shapeValue._def.innerType
      }
      if (shapeValue instanceof z.ZodNullable) {
        shapeValue = shapeValue._def.innerType
      }

      // Determine the type as a string based on Zod schema type
      let type: string | undefined
      if (shapeValue instanceof z.ZodString) {
        type = 'string'
      } else if (shapeValue instanceof z.ZodNumber) {
        type = 'number'
      } else if (shapeValue instanceof z.ZodBoolean) {
        type = 'boolean'
      } else if (shapeValue instanceof z.ZodDate) {
        type = 'date'
      } else if (shapeValue instanceof z.ZodEnum) {
        type = 'enum'
      } else if (shapeValue instanceof z.ZodArray) {
        const elementType = shapeValue._def.type
        if (elementType instanceof z.ZodString) {
          type = 'array of strings'
        } else if (elementType instanceof z.ZodNumber) {
          type = 'array of numbers'
        } else if (elementType instanceof z.ZodBoolean) {
          type = 'array of booleans'
        } else if (elementType instanceof z.ZodDate) {
          type = 'array of dates'
        } else if (elementType instanceof z.ZodObject) {
          type = 'array of objects'
        } else if (elementType instanceof z.ZodEnum) {
          type = 'array of enums'
        } else {
          type = 'array of unknowns'
        }
      } else if (shapeValue instanceof z.ZodObject) {
        type = 'object'
      } else if (shapeValue instanceof z.ZodTuple) {
        type = 'tuple'
      } else {
        type = 'unknown'
      }

      return [key, type]
    }),
  ) as { [K in keyof T]: string }
}

/**
 * Validates a query parameter value against a Zod schema field.
 * Returns the validated and parsed value, or null if validation fails.
 *
 * @example
 * const schema = z.object({
 *   dateRange: z.tuple([z.string().date(), z.string().date()]).nullable(),
 *   statuses: z.array(z.string()).default([]),
 * })
 *
 * // Validate a date range tuple
 * parseQueryParam('2024-01-01,2024-12-31', schema.shape.dateRange)
 * // Returns: ['2024-01-01', '2024-12-31']
 *
 * // Validate an array
 * parseQueryParam('paid,pending', schema.shape.statuses)
 * // Returns: ['paid', 'pending']
 */
export const parseQueryParam = <TSchema extends z.ZodTypeAny>(
  queryValue: string | string[] | undefined,
  schemaField: TSchema,
): z.infer<TSchema> | null => {
  if (!queryValue) {
    return null
  }

  // Unwrap nullable and default types to get the core schema
  let coreSchema: z.ZodTypeAny = schemaField

  // Keep unwrapping until we get to the core type
  while (
    coreSchema instanceof z.ZodNullable ||
    coreSchema instanceof z.ZodDefault
  ) {
    if (coreSchema instanceof z.ZodNullable) {
      coreSchema = coreSchema._def.innerType
    } else if (coreSchema instanceof z.ZodDefault) {
      coreSchema = coreSchema._def.innerType
    }
  }

  // Convert query value to array for consistent processing
  let values: string[]
  if (Array.isArray(queryValue)) {
    values = queryValue.filter((v): v is string => typeof v === 'string')
  } else if (typeof queryValue === 'string') {
    values = queryValue.split(',').map((value) => value.trim())
  } else {
    return null
  }

  // Handle different schema types
  try {
    // Array schema
    if (coreSchema instanceof z.ZodArray) {
      if (values.length === 0) {
        return null
      }
      return coreSchema.parse(values)
    }

    // Tuple schema (e.g., date ranges)
    if (coreSchema instanceof z.ZodTuple) {
      const items = coreSchema._def.items
      if (values.length !== items.length) {
        return null
      }
      return coreSchema.parse(values)
    }

    // String schema
    if (coreSchema instanceof z.ZodString && values.length === 1) {
      return coreSchema.parse(values[0])
    }

    return null
  } catch (_error) {
    return null
  }
}
